import "@hotwired/turbo-rails";
import Vue from "vue";
import VueTour from "vue-tour";
import VueClipboard from "vue-clipboard2";
import VueMask from "v-mask";
import VModal from "vue-js-modal";
import Lodash from "lodash";
import Fuse from "fuse.js";
import InfiniteLoading from "vue-infinite-loading";
import VueObserveVisibility from "vue-observe-visibility";
import VueEasytable from "vue-easytable";

import "vue-easytable/libs/theme-default/index.css";
import "vue-tour/dist/vue-tour.css";

import At from "vue-at";
import VueJsonPretty from "vue-json-pretty";
import VueSplit from "vue-split-panel";
import draggable from "vuedraggable";
import OtpInput from "@bachdgvn/vue-otp-input";
import Controller from "@/controllers/controller.vue";

// Import SVG utilities
import {
  getSvgUrl,
  getDriverSvgUrl,
  getInYardSvgUrl,
  getRawSvg,
} from "@/default/utils/svgs";
import { permissionComputed } from "@/default/utils/computed";
import rootWindow from "@/default/utils/root_window";

const root = rootWindow();

Turbo.config.forms.mode = "optin";
Turbo.session.drive = false;

Vue.use(VueTour);
Vue.use(VueClipboard);
Vue.use(VueMask);
Vue.use(VModal, { dynamic: true, injectModalsContainer: true });
Vue.use(VueSplit);
Vue.use(InfiniteLoading);
Vue.use(VueObserveVisibility);
Vue.use(VueEasytable);

Vue.component("at", At);
Vue.component("vue-json-pretty", VueJsonPretty);
Vue.component("v-split", VueSplit.Split);
Vue.component("v-split-area", VueSplit.SplitArea);
Vue.component("draggable", draggable);
Vue.component("v-otp-input", OtpInput);
Vue.component("controller", Controller);

import "@/default/eleme";
import "@/default/umy";
import "@/default/components";
import "@/system/components";
import "@/default/stores";
import "@/default/utils/core";
import http from "@/default/utils/http";
import http2 from "@/default/utils/http2";
import qs from "qs";
import "vue-json-pretty/lib/styles.css";
import * as Sentry from "@sentry/vue";

if (import.meta.env.MODE === "production") {
  Sentry.init({
    Vue,
    dsn: "https://396e8ae61d6742c7ad17b7a0f6dca408@o314017.ingest.sentry.io/4505329729077248",
    logErrors: true,
  });
}

Object.defineProperty(Vue.prototype, "_", { value: Lodash });
Vue.prototype.$search = function (term, list, options) {
  var fuse = new Fuse(list, options);
  return fuse.search(term);
};
Vue.prototype.$http = http;
Vue.prototype.$http2 = http2;
Vue.prototype.$veLoading = VueEasytable.VeLoading;
Object.defineProperty(Vue.prototype, "EGuide", {
  get() {
    return window.EGuide;
  },
});

Vue.prototype.$getSvgUrl = getSvgUrl;
Vue.prototype.$getDriverSvgUrl = getDriverSvgUrl;
Vue.prototype.$getInYardSvgUrl = getInYardSvgUrl;
Vue.prototype.$getRawSvg = getRawSvg;

window.SimpleNotify = function (data) {
  Vue.prototype.$notify({
    title: data.title,
    message: data.message,
    position: "bottom-left",
    customClass: "dense",
  });
  window.PlaySound();
};

window.PlaySound = function () {
  const audio = new Audio("/quick.mp3");
  audio
    .play()
    .then(() => {})
    .catch((error) => console.log(error));
};

window.mobileAgent = function () {
  // 1. Use modern Client Hints API if available (Chrome, Edge, Opera)
  if (navigator.userAgentData) {
    return navigator.userAgentData.mobile;
  }

  // 2. Identify touch-capable devices (like iPads or touch laptops)
  const hasTouch =
    window.matchMedia("(pointer: coarse)").matches ||
    navigator.maxTouchPoints > 0;

  // 3. Check the User Agent string for explicit mobile flags
  const hasMobileUA = /Mobi|Android|iPhone|iPad|iPod/i.test(
    navigator.navigator.userAgent || navigator.userAgent,
  );

  // 4. Critical fix: Exclude Mac/Windows/Linux desktop hardware even if they have touch screens
  const isDesktopOS = /Win64|Win32|Macintosh|Linux x86_64/i.test(
    navigator.userAgent,
  );

  // It's mobile if it has a mobile UA, OR if it has a touch screen but is NOT a desktop OS
  return hasMobileUA || (hasTouch && !isDesktopOS);
};

import { format } from "date-fns";
import { enableStopBack, releaseStopBack } from "@/default/utils/stop_back";

Vue.mixin({
  computed: {
    ...permissionComputed,
  },
  beforeDestroy() {
    if (this._stopBackEnabled) {
      releaseStopBack();
      this._stopBackEnabled = false;
    }
  },
  methods: {
    stopBack() {
      if (!this._stopBackEnabled) {
        enableStopBack();
        this._stopBackEnabled = true;
      }
    },
    prefixKey(name, id) {
      return `${name}-${id}`;
    },
    formatDate(value, options = { format: "yyyy-MM-dd" }) {
      let dt = value;
      if (!dt) return null;
      if (typeof value == "string") dt = new Date(value);
      let dtOnly = new Date(dt.valueOf() + dt.getTimezoneOffset() * 60 * 1000);
      return format(dtOnly, options.format);
    },
    mmdd(value) {
      return value ? this.formatDate(value, { format: "MM/dd" }) : null;
    },
    isImport(item) {
      const type = item.hasOwnProperty("type") ? item.type : item;
      return type.toLowerCase().includes("import");
    },
    isExport(item) {
      const type = item.hasOwnProperty("type") ? item.type : item;
      return type.toLowerCase().includes("export");
    },
    isXtown(item) {
      const type = item.hasOwnProperty("type") ? item.type : item;
      return type.toLowerCase().includes("xtown");
    },
    toUrl(url, params) {
      return (
        url +
        "?" +
        qs.stringify(params, {
          arrayFormat: "brackets",
          encode: false,
        })
      );
    },
    openUrl(url, blank = true) {
      if (blank) {
        window.open(url);
      } else {
        window.location.href = url;
      }
    },
    openImage(img) {
      const url = img.url || `/images/${img.uuid}`;
      NApp.newPopup(url, "ViewDocument", 960, 800, 0, 0);
    },
    reloadPage() {
      window.location.reload();
    },
    nestedAttrs(record, name, key = null) {
      const attrs = {};
      const data = Object.assign({}, record);
      data[name].forEach(
        (attr, index) => (attrs[key ? attr[key] : index] = attr),
      );
      delete data[name];
      Object.assign(data, { [name + "_attributes"]: attrs });
      return data;
    },
    viewOrderOptions(id, params = {}) {
      const regexp = new RegExp(`containers/${id}(/edit)?`);
      const denied = document.querySelector("#access-denied");
      if (regexp.test(window.location.pathname) && !denied) {
        return { disabled: { warning: "You are already in this order." } };
      } else {
        const searchParams = new URLSearchParams(params);
        return {
          title: `Order: ${id}`,
          url: [`/containers/${id}`, searchParams.toString()].join("?"),
        };
      }
    },
    editOrderOptions(id, params = {}) {
      const regexp = new RegExp(`containers/${id}(/edit)?`);
      const denied = document.querySelector("#access-denied");
      if (regexp.test(window.location.pathname) && !denied) {
        return { disabled: { warning: "You are already in this order." } };
      } else {
        const searchParams = new URLSearchParams(params);
        return {
          title: `Order: ${id}`,
          url: [`/containers/${id}/edit`, searchParams.toString()].join("?"),
        };
      }
    },
    isEmptyData(data) {
      return this._.isEmpty(data) && !this._.isNumber(data);
    },
    sumArray(array, fixed = 2) {
      return this._.sum(array.map((value) => parseFloat(value) || 0)).toFixed(
        fixed,
      );
    },
    splitStr(str, delimiter = ";") {
      return str.split(delimiter);
    },
    joinErrors(obj) {
      Object.keys(obj).map((key) => (obj[key] = obj[key].join(", ")));
      return obj;
    },
    getDownload(url, params) {
      this.$http
        .get(url, { params: params }, { responseType: "arraybuffer" })
        .then((response) => {
          this.downloadBlob(response);
        })
        .catch((err) => {});
    },
    postDownload(url, params) {
      this.$http
        .post(url, params, { responseType: "arraybuffer" })
        .then((response) => {
          this.downloadBlob(response);
        })
        .catch((err) => {});
    },
    downloadBlob(response) {
      let blob = new Blob([response.data], {
          type: response.headers["content-type"],
        }),
        filename = (response.headers["content-disposition"] || "").split(
          "filename=",
        )[1],
        link = document.createElement("a");
      filename = filename.replace(/"/g, "").split(/;/)[0];
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      link.click();
    },
  },
});

Vue.filter(
  "withDelimiter",
  function (value, reverse = false, options = { min: 2, max: 2 }) {
    if (!["number", "string"].includes(typeof value)) {
      return value;
    }
    if (!value.toString().match(/^-?\d*\.?\d*$/)) {
      return value;
    }
    if (reverse) {
      value = value * -1;
    }
    var formatter = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: options.min,
      maximumFractionDigits: options.max,
    });
    return formatter.format(value);
  },
);

Vue.filter("toPercentage", function (value, options = { min: 2, max: 2 }) {
  if (!["number", "string"].includes(typeof value)) return value;
  if (!value.toString().match(/^-?\d*\.?\d*$/)) return value;
  value = parseFloat(value) * 100;
  var formatter = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: options.min,
    maximumFractionDigits: options.max,
  });
  return `${formatter.format(value)}%`;
});

Vue.filter("toCurrency", function (value, options = { min: 2, max: 2 }) {
  if (!["number", "string"].includes(typeof value)) return value;
  if (!value.toString().match(/^-?\d*\.?\d*$/)) return value;
  var formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: options.min,
    maximumFractionDigits: options.max,
  });
  return formatter.format(value);
});
// new Date(dt.valueOf() + dt.getTimezoneOffset() * 60 * 1000)
Vue.filter("usFormat", function (value, options = { year: true }) {
  if (!["string"].includes(typeof value)) return value;
  let date = value.match(/\d+-\d+-\d+/);
  if (date == null || date.length == 0) return value;
  let each = date[0].split("-");
  let parts = [each[1], each[2]];
  if (options.year == true) parts.push(each[0]);
  if (options.year == "yy") parts.push(each[0].substr(2, 2));
  return parts.join("/") + value.replace(date[0], "");
});

Vue.filter("toFixed", function (value, options = { digits: 2 }) {
  if (typeof value == "string") value = parseFloat(value);
  return Number.isFinite(value) ? value.toFixed(options.digits) : null;
});

function initVues() {
  const selector = "[data-behavior='vue']:not(.vued)";
  [...document.querySelectorAll(selector)].map((element) => {
    return new Promise((resolve, reject) => {
      if (!element.classList.contains("vued")) {
        element.classList.add("vued");
        // nested data-behavior='vue', just init once from top level
        element
          .querySelectorAll(selector)
          .forEach((element) => element.classList.add("vued"));
        root.vues.push(new Vue({ el: element }));
      }
      resolve(element);
    });
  });
}

// init magic dialog component
function initDialog() {
  const edialog = document.querySelector("#e-dialog");
  if (!edialog) {
    let dialog = document.createElement("e-dialog");
    document.body.appendChild(dialog);
    new Vue({ el: dialog });
  }
}

function initGuide() {
  const eguide = document.querySelector("#e-guide");
  if (!eguide) {
    let guide = document.createElement("e-guide");
    document.body.appendChild(guide);
    new Vue({ el: guide });
  }
}

function initTurboFrames() {
  document.querySelectorAll("turbo-frame[data-src]").forEach((frame) => {
    frame.src = frame.dataset.src;
    delete frame.dataset.src;
  });
}

root.vues = [];
document.addEventListener("turbo:load", initVues);
document.addEventListener("turbo:load", initDialog);
document.addEventListener("turbo:load", initGuide);
document.addEventListener("turbo:load", initTurboFrames);
document.addEventListener("turbo:render", () => {
  root.vues.forEach((vue) => vue.$destroy());
});
// Turbo 8: disconnecting <turbo-frame> (Vue modal teardown) aborts the in-flight fetch; the frame
// controller does not await perform(), so AbortError can surface as an unhandled rejection. Expected.
window.addEventListener("unhandledrejection", (event) => {
  const reason = event.reason;
  if (
    reason &&
    (reason.name === "AbortError" ||
      (typeof DOMException !== "undefined" &&
        reason instanceof DOMException &&
        reason.name === "AbortError"))
  ) {
    event.preventDefault();
  }
});
window.addEventListener("keyup", (event) => {
  if (event.ctrlKey && event.key === "k") {
    let detail = {};
    const connector = (root.taskConnectors || []).last();
    if (connector) {
      const [taskable_type, taskable_id, name] = connector.split(":");
      detail = { taskable_type, taskable_id, name };
    }
    root.dispatchEvent(new CustomEvent("TaskModal", { detail }));
  }
});

// turbo:load can fire before this deferred module finishes loading (Vite cold start,
// Safari, slow networks). If the document is already past "loading", re-dispatch so
// initVues still runs. Safe to double-fire: initVues is idempotent via the .vued class.
if (document.readyState !== "loading") {
  document.documentElement.dispatchEvent(
    new CustomEvent("turbo:load", {
      bubbles: true,
      cancelable: true,
    }),
  );
}
